import { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import { Loader2 } from 'lucide-react';
import { PageHeader } from '@/shared/components';
import { useBudgetReportYearsListQuery } from '@/features/budgets/hooks/useBudgetQueries';
import type { DashboardSettingDto } from '../api/dashboardSettings.api';
import { DashboardSettingField } from '../components/DashboardSettingField';
import {
  DASHBOARD_SETTING_SECTIONS,
  fieldsForSection,
  type DashboardSettingSectionId,
} from '../dashboardSettingNames';
import {
  useDashboardSettingsMap,
  useUpdateDashboardSettingMutation,
} from '../hooks/useDashboardSettingsQuery';
import {
  serializeFieldValue,
  validateSectionFields,
} from '../utils/dashboardSettingValidation';

function savedValueForField(
  map: Map<string, DashboardSettingDto>,
  name: string,
  defaultValue: string
): string {
  return map.get(name)?.value ?? defaultValue;
}

function sectionHasChanges(
  sectionId: DashboardSettingSectionId,
  map: Map<string, DashboardSettingDto>,
  drafts: Record<string, string>
): boolean {
  return fieldsForSection(sectionId).some((field) => {
    const saved = savedValueForField(map, field.name, field.defaultValue);
    const display = drafts[field.name] ?? saved;
    return display !== saved;
  });
}

function sectionDraftValues(
  sectionId: DashboardSettingSectionId,
  map: Map<string, DashboardSettingDto>,
  drafts: Record<string, string>
): Record<string, string> {
  const values: Record<string, string> = {};
  for (const field of fieldsForSection(sectionId)) {
    const saved = savedValueForField(map, field.name, field.defaultValue);
    values[field.name] = drafts[field.name] ?? saved;
  }
  return values;
}

function sectionMissingFields(
  sectionId: DashboardSettingSectionId,
  map: Map<string, DashboardSettingDto>
): string[] {
  return fieldsForSection(sectionId)
    .filter((f) => !map.has(f.name))
    .map((f) => f.name);
}

export function DashboardSettingsPage() {
  const { map, isLoading: settingsLoading } = useDashboardSettingsMap();
  const yearsQuery = useBudgetReportYearsListQuery();
  const updateMutation = useUpdateDashboardSettingMutation();

  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [sectionErrors, setSectionErrors] = useState<Record<string, Record<string, string>>>({});
  const [savingSection, setSavingSection] = useState<DashboardSettingSectionId | null>(null);

  useEffect(() => {
    setDrafts({});
    setSectionErrors({});
  }, [map]);

  const yearOptions = useMemo(() => {
    const raw = yearsQuery.data ?? [];
    return [...raw].sort((a, b) => b - a).map((n) => ({ value: String(n), label: String(n) }));
  }, [yearsQuery.data]);

  const handleFieldChange = useCallback((name: string, value: string) => {
    setDrafts((prev) => ({ ...prev, [name]: value }));
    setSectionErrors((prev) => {
      const next = { ...prev };
      for (const section of DASHBOARD_SETTING_SECTIONS) {
        if (fieldsForSection(section.id).some((f) => f.name === name)) {
          const sectionErrs = { ...(next[section.id] ?? {}) };
          delete sectionErrs[name];
          next[section.id] = sectionErrs;
        }
      }
      return next;
    });
  }, []);

  const handleSaveSection = async (sectionId: DashboardSettingSectionId) => {
    const fields = fieldsForSection(sectionId);
    const values = sectionDraftValues(sectionId, map, drafts);
    const errors = validateSectionFields(fields, values);
    if (Object.keys(errors).length > 0) {
      setSectionErrors((prev) => ({ ...prev, [sectionId]: errors }));
      return;
    }

    const missing = sectionMissingFields(sectionId, map);
    if (missing.length > 0) {
      message.error('Cannot save — one or more settings are missing from the database.');
      return;
    }

    const toUpdate = fields.filter((field) => {
      const saved = savedValueForField(map, field.name, field.defaultValue);
      const display = values[field.name];
      return display !== saved;
    });

    if (toUpdate.length === 0) return;

    setSavingSection(sectionId);
    try {
      for (const field of toUpdate) {
        const dto = map.get(field.name);
        if (!dto) continue;
        await updateMutation.mutateAsync({
          id: dto.id,
          name: field.name,
          value: serializeFieldValue(field, values[field.name]),
        });
      }
      setDrafts((prev) => {
        const next = { ...prev };
        for (const field of toUpdate) {
          delete next[field.name];
        }
        return next;
      });
      message.success('Settings updated successfully.');
    } catch {
      message.error('Failed to update settings.');
    } finally {
      setSavingSection(null);
    }
  };

  const isLoading = settingsLoading || yearsQuery.isLoading;

  return (
    <div className="mx-auto max-w-3xl space-y-6 px-4 pb-16 pt-8 sm:px-6 lg:px-8">
      <PageHeader
        title="Dashboard settings"
        description="Configure default values and report thresholds used across the application."
      />

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="size-8 animate-spin text-indigo-600" />
        </div>
      ) : (
        DASHBOARD_SETTING_SECTIONS.map((section) => {
          const fields = fieldsForSection(section.id);
          const values = sectionDraftValues(section.id, map, drafts);
          const errors = sectionErrors[section.id] ?? {};
          const hasChanges = sectionHasChanges(section.id, map, drafts);
          const missing = sectionMissingFields(section.id, map);
          const validationErrors = validateSectionFields(fields, values);
          const hasValidationErrors = Object.keys(validationErrors).length > 0;
          const isSaving = savingSection === section.id && updateMutation.isPending;

          return (
            <section
              key={section.id}
              className="rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm ring-1 ring-slate-100"
            >
              <h2 className="text-base font-semibold text-slate-900">{section.title}</h2>
              <p className="mt-1 text-sm text-slate-500">{section.description}</p>

              <div className="mt-6 space-y-6">
                {fields.map((field) => {
                  const saved = savedValueForField(map, field.name, field.defaultValue);
                  const display = drafts[field.name] ?? saved;
                  return (
                    <DashboardSettingField
                      key={field.name}
                      field={field}
                      value={display}
                      savedValue={saved}
                      error={errors[field.name]}
                      missing={!map.has(field.name)}
                      disabled={isSaving}
                      yearOptions={field.kind === 'year' ? yearOptions : undefined}
                      onChange={(v) => handleFieldChange(field.name, v)}
                    />
                  );
                })}
              </div>

              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => handleSaveSection(section.id)}
                  disabled={
                    !hasChanges ||
                    isSaving ||
                    missing.length > 0 ||
                    hasValidationErrors
                  }
                  className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-4 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isSaving && <Loader2 className="size-4 animate-spin" />}
                  {isSaving ? 'Saving…' : 'Update'}
                </button>
              </div>

              {missing.length > 0 && (
                <p className="mt-4 text-sm text-amber-700">
                  {missing.length === 1
                    ? `Setting "${missing[0]}" is missing from the database.`
                    : `${missing.length} settings in this section are missing from the database.`}
                </p>
              )}
            </section>
          );
        })
      )}
    </div>
  );
}
